<template>
  <div class="contents">
    <div class="container-fluid">
      <div class="breadcrumb-main user-member justify-content-sm-between mb-3">
        <h4 class="text-capitalize fw-500 breadcrumb-title">Transfer Saldo Antar Akun</h4>
      </div>

      <div class="card p-4 border bg-white radius-xl" style="max-width: 720px;">
        <p class="text-muted mb-3">
          Cari dan konfirmasi akun asal & tujuan (lihat saldo), lalu isi nominal transfer. Kedua user akan menerima email notifikasi.
        </p>

        <div class="border rounded p-3 mb-3">
          <label class="fw-500">Akun asal (A) *</label>
          <div class="input-group mt-2">
            <input
              v-model="fromQuery"
              type="text"
              class="form-control"
              placeholder="Email atau ID user"
              :disabled="lookupFromLoading"
              @keyup.enter="lookupFrom"
            >
            <div class="input-group-append">
              <button class="btn btn-outline-primary" type="button" :disabled="lookupFromLoading || !fromQuery.trim()" @click="lookupFrom">
                {{ lookupFromLoading ? '...' : 'Cari' }}
              </button>
            </div>
          </div>
          <div v-if="fromAccount" class="alert alert-light border mt-3 mb-0 py-2">
            <div><strong>{{ fromAccount.name || '—' }}</strong></div>
            <div class="text-muted small">ID {{ fromAccount.user_id }} · {{ fromAccount.email }}</div>
            <div class="mt-1">Saldo: <strong>{{ formatRupiah(fromAccount.balance) }}</strong></div>
            <button type="button" class="btn btn-link btn-sm px-0 mt-1" @click="clearFrom">Ganti akun</button>
          </div>
        </div>

        <div class="border rounded p-3 mb-3">
          <label class="fw-500">Akun tujuan (B) *</label>
          <div class="input-group mt-2">
            <input
              v-model="toQuery"
              type="text"
              class="form-control"
              placeholder="Email atau ID user"
              :disabled="lookupToLoading"
              @keyup.enter="lookupTo"
            >
            <div class="input-group-append">
              <button class="btn btn-outline-primary" type="button" :disabled="lookupToLoading || !toQuery.trim()" @click="lookupTo">
                {{ lookupToLoading ? '...' : 'Cari' }}
              </button>
            </div>
          </div>
          <div v-if="toAccount" class="alert alert-light border mt-3 mb-0 py-2">
            <div><strong>{{ toAccount.name || '—' }}</strong></div>
            <div class="text-muted small">ID {{ toAccount.user_id }} · {{ toAccount.email }}</div>
            <div class="mt-1">Saldo: <strong>{{ formatRupiah(toAccount.balance) }}</strong></div>
            <button type="button" class="btn btn-link btn-sm px-0 mt-1" @click="clearTo">Ganti akun</button>
          </div>
        </div>

        <div class="form-group">
          <label>Nominal transfer (Rp) *</label>
          <input
            v-model.number="form.amount"
            type="number"
            min="1"
            class="form-control"
            placeholder="100000"
            :disabled="!canEnterAmount"
          >
          <small v-if="fromAccount && form.amount > 0 && form.amount > fromAccount.balance" class="text-danger">
            Nominal melebihi saldo asal (maks {{ formatRupiah(fromAccount.balance) }})
          </small>
          <small v-else-if="!canEnterAmount" class="text-muted">Isi dan cari kedua akun terlebih dahulu</small>
        </div>
        <div class="form-group">
          <label>Catatan (opsional)</label>
          <textarea v-model="form.note" class="form-control" rows="2" placeholder="Alasan transfer untuk audit" :disabled="!canEnterAmount" />
        </div>

        <button class="btn btn-primary" :disabled="processing || !canSubmit" @click="submit">
          {{ processing ? 'Memproses...' : 'Transfer Saldo' }}
        </button>

        <div v-if="result" class="alert alert-success mt-3 mb-0">
          Transfer berhasil.<br>
          Saldo asal baru: <b>{{ formatRupiah(result.from_new_balance) }}</b><br>
          Saldo tujuan baru: <b>{{ formatRupiah(result.to_new_balance) }}</b>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  data() {
    return {
      processing: false,
      lookupFromLoading: false,
      lookupToLoading: false,
      result: null,
      fromQuery: '',
      toQuery: '',
      fromAccount: null,
      toAccount: null,
      form: {
        amount: null,
        note: '',
      },
    };
  },
  computed: {
    canEnterAmount() {
      return !!(this.fromAccount && this.toAccount);
    },
    canSubmit() {
      if (!this.fromAccount || !this.toAccount) return false;
      if (this.fromAccount.user_id === this.toAccount.user_id) return false;
      if (!this.form.amount || this.form.amount <= 0) return false;
      if (this.form.amount > this.fromAccount.balance) return false;
      return true;
    },
  },
  methods: {
    lookupParams(query) {
      const q = String(query || '').trim();
      if (/^\d+$/.test(q)) {
        return { user_id: q };
      }
      return { email: q };
    },
    async lookupFrom() {
      if (!this.fromQuery.trim()) return;
      this.lookupFromLoading = true;
      this.fromAccount = null;
      this.result = null;
      try {
        const r = await axios.get('admin/balance-transfer/lookup', { params: this.lookupParams(this.fromQuery) });
        this.fromAccount = r.data.data;
        this.$toast.success(`Akun asal: ${this.fromAccount.email}`);
      } catch (e) {
        this.$toast.error(e.response?.data?.message || 'Akun asal tidak ditemukan');
      }
      this.lookupFromLoading = false;
    },
    async lookupTo() {
      if (!this.toQuery.trim()) return;
      this.lookupToLoading = true;
      this.toAccount = null;
      this.result = null;
      try {
        const r = await axios.get('admin/balance-transfer/lookup', { params: this.lookupParams(this.toQuery) });
        this.toAccount = r.data.data;
        this.$toast.success(`Akun tujuan: ${this.toAccount.email}`);
      } catch (e) {
        this.$toast.error(e.response?.data?.message || 'Akun tujuan tidak ditemukan');
      }
      this.lookupToLoading = false;
    },
    clearFrom() {
      this.fromAccount = null;
      this.fromQuery = '';
      this.form.amount = null;
      this.result = null;
    },
    clearTo() {
      this.toAccount = null;
      this.toQuery = '';
      this.result = null;
    },
    async submit() {
      if (!this.canSubmit) {
        this.$toast.error('Lengkapi akun asal/tujuan dan nominal yang valid');
        return;
      }
      this.processing = true;
      this.result = null;
      try {
        const r = await axios.post('admin/balance-transfer', {
          from_user_id: this.fromAccount.user_id,
          to_user_id: this.toAccount.user_id,
          amount: this.form.amount,
          note: this.form.note,
        });
        this.result = r.data.data || null;
        this.$toast.success(r.data.message || 'Transfer berhasil');
        if (this.fromAccount && this.result) {
          this.fromAccount = { ...this.fromAccount, balance: this.result.from_new_balance };
        }
        if (this.toAccount && this.result) {
          this.toAccount = { ...this.toAccount, balance: this.result.to_new_balance };
        }
        this.form.amount = null;
        this.form.note = '';
      } catch (e) {
        this.$toast.error(e.response?.data?.message || 'Gagal transfer saldo');
      }
      this.processing = false;
    },
    formatRupiah(v) {
      return 'Rp ' + (v || 0).toLocaleString('id-ID');
    },
  },
};
</script>
